import { useTracking } from '@folklore/tracking';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { ComponentType, useEffect, useState } from 'react';

import * as CardComponents from '../cards/promotions';
import Notification from './Notification';

import styles from '../../styles/popups/promotion-notification.module.css';

type PromotionNotificationProps = {
    promotion: Promotion;
    delay?: number | null;
    className?: string | null;
    onClosed?: (() => void) | null;
} & Record<string, unknown>;

function PromotionNotification({
    promotion,
    delay = 1000,
    className = null,
    onClosed = null,
    ...props
}: PromotionNotificationProps) {
    const [opened, setOpened] = useState(delay === null);
    const [hasOpened, setHasOpened] = useState(opened);
    const tracking = useTracking();

    const { id, handle, type = null, closeable = true, closeTrigger = null } = promotion;
    const PromotionComponent = getComponentFromName(
        CardComponents,
        type,
        CardComponents.Default,
    ) as ComponentType<Record<string, unknown>> | null;

    useEffect(() => {
        if (closeTrigger === null) {
            return () => {};
        }
        tracking.triggers.register(`promotion_popup_close_${id}`, closeTrigger, () => {
            setOpened(false);
        });
        return () => {
            tracking.triggers.unregister(`promotion_popup_close_${id}`);
        };
    }, [id, tracking, closeTrigger]);

    useEffect(() => {
        if (delay === null) {
            return () => {};
        }
        const timeout = setTimeout(() => {
            setOpened(true);
            setHasOpened(true);
        }, delay);
        return () => {
            clearTimeout(timeout);
        };
    }, [delay]);

    const onRequestClose = () => {
        setOpened(false);
        tracking.trackPromotion(promotion, 'close');
    };

    const onPromotionComplete = () => {
        setOpened(false);
        tracking.trackPromotion(promotion, 'complete');
    };

    const onPromotionCancel = () => {
        setOpened(false);
        tracking.trackPromotion(promotion, 'cancel');
    };

    return PromotionComponent !== null ? (
        <Notification
            id={`promotion-${handle || id}`}
            opened={opened}
            withTransition
            withoutCloseButton={!closeable}
            className={classNames([styles.container, className])}
            onClosed={hasOpened ? onClosed : null}
            requestClose={onRequestClose}
        >
            <PromotionComponent
                {...promotion}
                {...props}
                invert
                className={styles.card}
                onComplete={onPromotionComplete}
                onCancel={onPromotionCancel}
            />
        </Notification>
    ) : null;
}

export default PromotionNotification;
